import * as React from 'react';
import { cn } from '@/lib/utils';

type RibbonVariant = 'spicy' | 'new' | 'popular' | 'riceless' | 'scorched' | 'mini';
type RibbonPosition = 'left' | 'right';

const variantStyles: Record<RibbonVariant, { bg: string; shadowColor: string }> = {
  spicy: {
    bg: 'from-[#D93B4E] to-[#EF5065]',
    shadowColor: 'rgba(217,59,78,0.35)',
  },
  new: {
    bg: 'from-[#EC4899] to-[#F472B6]',
    shadowColor: 'rgba(244,114,182,0.35)',
  },
  popular: {
    bg: 'from-[#6225E6] to-[var(--primary)]',
    shadowColor: 'rgba(123,66,255,0.35)',
  },
  riceless: {
    bg: 'from-[#0D9488] to-[#2DD4BF]',
    shadowColor: 'rgba(45,212,191,0.35)',
  },
  scorched: {
    bg: 'from-[#D97706] to-[#FBBF24]',
    shadowColor: 'rgba(251,191,36,0.35)',
  },
  mini: {
    bg: 'from-[#0891B2] to-[#22D3EE]',
    shadowColor: 'rgba(34,211,238,0.35)',
  },
};

const variantLabels: Record<RibbonVariant, string> = {
  spicy: 'Острое',
  new: 'Новинка',
  popular: 'Хит',
  riceless: 'Без риса',
  scorched: 'Опалённый',
  mini: 'Мини',
};

export interface CornerRibbonProps {
  variant: RibbonVariant;
  position?: RibbonPosition;
  className?: string;
}

const CornerRibbon = React.memo<CornerRibbonProps>(({ variant, position = 'left', className }) => {
  const config = variantStyles[variant];
  const label = variantLabels[variant];

  return (
    <div
      className={cn(
        'absolute top-0 z-10',
        position === 'left' ? 'left-0 rounded-br-card' : 'right-0 rounded-bl-card',
        'bg-gradient-to-r',
        config.bg,
        'text-white font-bold',
        // 8/9px оставлены как есть: нижняя ступень шкалы — 10 (t-nano), и
        // владелец отдельно предупреждён, что подъём мелких плашек с 8-10 до
        // 11 ломает текст в самых узких местах. Лента режется по углу картинки,
        // запаса по ширине у неё нет.
        'text-[8px] md:text-[9px] tracking-wider uppercase',
        'px-2.5 py-[4px] md:px-3 md:py-[5px]',
        'pointer-events-none select-none',
        className
      )}
      style={{
        boxShadow: position === 'left'
          ? `2px 2px 8px ${config.shadowColor}`
          : `-2px 2px 8px ${config.shadowColor}`,
      }}
    >
      {/* Блик */}
      <span
        aria-hidden
        className="absolute inset-x-[8%] top-[1px] h-[40%] rounded-pill bg-white/20 pointer-events-none"
      />
      <span className="relative z-[1]">{label}</span>
    </div>
  );
});
CornerRibbon.displayName = 'CornerRibbon';

export { CornerRibbon };
